import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface MetricsCardProps {
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  description?: string;
  trend?: {
    value: number;
    direction: "up" | "down" | "neutral";
  };
  className?: string;
}

export function MetricsCard({
  title,
  value,
  icon,
  description,
  trend,
  className,
}: MetricsCardProps) {
  return (
    <Card className={cn("metrics-card overflow-hidden border-0", className)}>
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0 px-0 pt-0">
        <CardTitle className="text-sm font-medium text-gray-600">
          {title}
        </CardTitle>
        <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
          {icon}
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        <div className="text-2xl font-bold text-gray-800">{value}</div>
        {description && (
          <p className="text-xs text-black mt-1">{description}</p>
        )}
        {trend && (
          <div className="flex items-center mt-2">
            <span
              className={cn("text-xs font-medium flex items-center", {
                "text-green-600": trend.direction === "up",
                "text-red-600": trend.direction === "down",
                "text-gray-500": trend.direction === "neutral",
              })}
            >
              {trend.direction === "up" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 mr-1"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 7a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L12 10.586V7z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : trend.direction === "down" ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 mr-1"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 13a1 1 0 10-2 0v-3.586l-1.293 1.293a1 1 0 01-1.414-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L12 9.414V13z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : null}
              {trend.value}%
            </span>
            <span className="text-xs text-gray-500 ml-1">from last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
